import React, { useEffect, useRef, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material";
import useEditorMapa, { GUARDADO } from "../../stores/useEditorMapa";
import useEventoStore from "../../stores/useEventoStore";
import {
  actualizarSector,
  actualizarDecorativo,
  quitarSector,
  quitarDecorativo,
  duplicarSector,
  sePuedeBorrar,
} from "../../domain/mapa/mapa";
import { quitarMesa, duplicarMesa, moverMesa, ajustarFila } from "../../domain/mapa/generador";
import { cambiarSector, borrarVarios } from "../../components/mapa/acciones";
import { moverObjetos } from "../../domain/mapa/seleccion";
import BarraEditor from "../../components/mapa/BarraEditor";
import PanelAgregar from "../../components/mapa/PanelAgregar";
import PanelTarifas from "../../components/mapa/PanelTarifas";
import PanelPropiedades from "../../components/mapa/PanelPropiedades";
import LienzoMapa from "../../components/mapa/LienzoMapa";
import { COLOR } from "../../components/mapa/estilos";

const ESPERA_AUTOGUARDADO = 2000;
const ESPERA_REINTENTO = 8000;
const ANCHO_MINIMO = 900;

const FLECHAS = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };

const esCampoDeTexto = (el) => el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);

const useAutoguardado = () => {
  const guardado = useEditorMapa((s) => s.guardado);
  const mapa = useEditorMapa((s) => s.historial.presente);
  const guardarBorrador = useEditorMapa((s) => s.guardarBorrador);

  useEffect(() => {
    if (guardado !== GUARDADO.PENDIENTE && guardado !== GUARDADO.ERROR) return undefined;
    const espera = guardado === GUARDADO.ERROR ? ESPERA_REINTENTO : ESPERA_AUTOGUARDADO;
    const t = setTimeout(guardarBorrador, espera);
    return () => clearTimeout(t);
  }, [guardado, mapa, guardarBorrador]);

  useEffect(() => {
    if (guardado === GUARDADO.AL_DIA || guardado === GUARDADO.CONFLICTO) return undefined;
    const avisar = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", avisar);
    return () => window.removeEventListener("beforeunload", avisar);
  }, [guardado]);
};

const useAtajos = () => {
  useEffect(() => {
    const alApretar = (e) => {
      if (esCampoDeTexto(document.activeElement)) return;
      const estado = useEditorMapa.getState();
      const { seleccion, cambiar, seleccionar } = estado;
      const mapa = estado.historial.presente;
      const mod = e.ctrlKey || e.metaKey;
      const tecla = e.key.toLowerCase();

      if (mod && tecla === "z") {
        e.preventDefault();
        if (e.shiftKey) estado.rehacer();
        else estado.deshacer();
        return;
      }
      if (mod && tecla === "y") {
        e.preventDefault();
        estado.rehacer();
        return;
      }
      if (e.key === "Escape") {
        seleccionar(null);
        return;
      }
      if (!seleccion || !mapa || estado.pestana === "tarifas") return;

      if (seleccion.tipo === "varios") {
        if (e.key === "Delete" || e.key === "Backspace") {
          e.preventDefault();
          borrarVarios(seleccion.elementos);
        } else if (FLECHAS[e.key]) {
          e.preventDefault();
          const paso = e.shiftKey ? 10 : 1;
          const [dx, dy] = FLECHAS[e.key];
          cambiar((m) => moverObjetos(m, seleccion.elementos, dx * paso, dy * paso), { agrupar: "varios-flechas" });
        }
        return;
      }

      if (seleccion.tipo === "mesa") {
        const sector = mapa.sectores.find((s) => s.clave === seleccion.sector);
        const mesa = sector?.mesas.find((m) => m.clave === seleccion.clave);
        if (!mesa) return;
        if (mod && tecla === "d") {
          e.preventDefault();
          const r = cambiarSector(cambiar, sector.clave, (s) => duplicarMesa(s, mesa.clave));
          if (r?.clave) seleccionar({ tipo: "mesa", sector: sector.clave, clave: r.clave });
        } else if (e.key === "Delete" || e.key === "Backspace") {
          e.preventDefault();
          if (cambiarSector(cambiar, sector.clave, (s) => quitarMesa(s, mesa.clave))) {
            seleccionar({ tipo: "sector", clave: sector.clave });
          }
        } else if (FLECHAS[e.key]) {
          e.preventDefault();
          const paso = e.shiftKey ? 10 : 1;
          const [dx, dy] = FLECHAS[e.key];
          cambiarSector(cambiar, sector.clave, (s) => moverMesa(s, mesa.clave, { x: mesa.x + dx * paso, y: mesa.y + dy * paso }), {
            agrupar: `${mesa.clave}-flechas`,
          });
        }
        return;
      }

      if (seleccion.tipo === "fila") {
        if (FLECHAS[e.key]) {
          e.preventDefault();
          const paso = e.shiftKey ? 10 : 1;
          const [dx, dy] = FLECHAS[e.key];
          cambiarSector(cambiar, seleccion.sector, (s) => ajustarFila(s, seleccion.indice, { dx: dx * paso, dy: dy * paso }), {
            agrupar: `${seleccion.sector}-fila-${seleccion.indice}`,
          });
        }
        return;
      }

      const esSector = seleccion.tipo === "sector";
      const item = esSector
        ? mapa.sectores.find((s) => s.clave === seleccion.clave)
        : mapa.decorativos.find((d) => d.clave === seleccion.clave);
      if (!item) return;

      if (mod && tecla === "d" && esSector) {
        e.preventDefault();
        let clave = null;
        cambiar((m) => {
          const r = duplicarSector(m, item.clave);
          clave = r.clave;
          return r.mapa;
        });
        if (clave) seleccionar({ tipo: "sector", clave });
        return;
      }
      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        if (esSector && (!sePuedeBorrar(item) || (item.tarifas.length > 0 && !item.editor?.parteDe))) return;
        cambiar((m) => (esSector ? quitarSector(m, item.clave) : quitarDecorativo(m, item.clave)));
        seleccionar(null);
        return;
      }

      if (FLECHAS[e.key]) {
        e.preventDefault();
        const paso = e.shiftKey ? 10 : 1;
        const [dx, dy] = FLECHAS[e.key];
        const cambios = { x: item.x + dx * paso, y: item.y + dy * paso };
        if (!esSector && item.tipo === "linea") {
          cambios.punto_final_x = (item.punto_final_x ?? item.x) + dx * paso;
          cambios.punto_final_y = (item.punto_final_y ?? item.y) + dy * paso;
        }
        cambiar(
          (m) => (esSector ? actualizarSector(m, item.clave, cambios) : actualizarDecorativo(m, item.clave, cambios)),
          { agrupar: `${item.clave}-flechas` }
        );
      }
    };
    window.addEventListener("keydown", alApretar);
    return () => window.removeEventListener("keydown", alApretar);
  }, []);
};

const AvisoRespaldo = () => {
  const respaldo = useEditorMapa((s) => s.respaldo);
  const recuperar = useEditorMapa((s) => s.recuperarRespaldo);
  const descartar = useEditorMapa((s) => s.descartarRespaldo);
  if (!respaldo) return null;
  return (
    <Alert
      severity="warning"
      sx={{ borderRadius: 0 }}
      action={
        <>
          <Button color="inherit" size="small" onClick={recuperar} sx={{ textTransform: "none" }}>
            Recuperar
          </Button>
          <Button color="inherit" size="small" onClick={descartar} sx={{ textTransform: "none" }}>
            Descartar
          </Button>
        </>
      }
    >
      Hay cambios de esta computadora que no llegaron a guardarse ({new Date(respaldo.fecha).toLocaleString("es-AR")}).
    </Alert>
  );
};

export const EditorMapa = () => {
  const { id } = useParams();
  const history = useHistory();
  const lienzoRef = useRef(null);
  const cargar = useEditorMapa((s) => s.cargar);
  const cargando = useEditorMapa((s) => s.cargando);
  const errorCarga = useEditorMapa((s) => s.errorCarga);
  const hayMapa = useEditorMapa((s) => Boolean(s.historial.presente));
  const pestana = useEditorMapa((s) => s.pestana);
  const [nombreEvento, setNombreEvento] = useState("");
  const [angosta, setAngosta] = useState(window.innerWidth < ANCHO_MINIMO);

  useEffect(() => {
    cargar(id);
    useEventoStore
      .getState()
      .fetchEvento(id)
      .then((data) => setNombreEvento(data?.evento?.evento || ""))
      .catch(() => {});
  }, [id, cargar]);

  // el editor tapa toda la página: que la de atrás no muestre su scroll
  useEffect(() => {
    const anterior = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = anterior;
    };
  }, []);

  useEffect(() => {
    const alCambiar = () => setAngosta(window.innerWidth < ANCHO_MINIMO);
    window.addEventListener("resize", alCambiar);
    return () => window.removeEventListener("resize", alCambiar);
  }, []);

  useAutoguardado();
  useAtajos();

  let contenido;
  if (angosta) {
    contenido = (
      <Box sx={{ m: "auto", p: 3, textAlign: "center", maxWidth: 420 }}>
        <Typography sx={{ fontWeight: 700, color: COLOR.titulo, mb: 1 }}>El editor del mapa necesita una pantalla más grande</Typography>
        <Typography sx={{ fontSize: 14, color: COLOR.textoSuave, mb: 2 }}>Abrilo desde una computadora para diseñar el mapa.</Typography>
        <Button onClick={() => history.push(`/eventos/${id}/ubicaciones`)} sx={{ textTransform: "none" }}>
          Volver
        </Button>
      </Box>
    );
  } else if (cargando || !hayMapa) {
    contenido = errorCarga ? (
      <Box sx={{ m: "auto", textAlign: "center" }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorCarga}
        </Alert>
        <Button onClick={() => history.push(`/eventos/${id}/ubicaciones`)} sx={{ textTransform: "none" }}>
          Volver
        </Button>
      </Box>
    ) : (
      <Box sx={{ m: "auto" }}>
        <CircularProgress />
      </Box>
    );
  } else {
    contenido = (
      <>
        <BarraEditor eventoId={id} nombreEvento={nombreEvento} />
        <AvisoRespaldo />
        <Box sx={{ flex: 1, display: "flex", minHeight: 0 }}>
          {pestana === "tarifas" ? <PanelTarifas /> : <PanelAgregar lienzoRef={lienzoRef} />}
          <LienzoMapa ref={lienzoRef} />
          <PanelPropiedades />
        </Box>
      </>
    );
  }

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1250,
        display: "flex",
        flexDirection: "column",
        bgcolor: COLOR.fondoLienzo,
        fontFamily: "inherit",
      }}
    >
      {contenido}
    </Box>
  );
};

export default EditorMapa;
